// Dashboard live widgets — 10 sn polling
// 1. Live Feed (session start/end)
// 2. Aktive Arbeiter (şu an çalışanlar + süre)
// 3. Abteilung Heatmap (abteilungda kaç aktif/bugün fertig)

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { api, type FeedEvent, type HeatmapEntry, type ArbeitSession, type ProblemMeldung, type ProblemCount } from '../lib/api';
import { ErrorBanner } from './ErrorBanner';

// External-data pattern: wenn prop gesetzt, kein internes Polling (Dashboard-consolidation).
interface ExternalProps<T> {
  externalData?: T;
  externalError?: unknown;
  externalReload?: () => void;
}

const MODULE_LABELS: Record<string, string> = {
  drueckteile: 'Drückteile', laufraeder: 'Laufräder',
  baugruppen: 'Baugruppen', montage: 'Montage',
};

export function LiveFeedWidget(props: ExternalProps<{ data: FeedEvent[] }> = {}) {
  const [modul, setModul] = useState<string>('');
  const isExt = props.externalData !== undefined;
  const internal = useApi(
    () => api.sessions.feed({ limit: 25, modul: modul || undefined }),
    [modul],
    !isExt
  );
  useAutoRefresh(() => internal.reload(), 5_000, !isExt);
  const feed = {
    data: isExt ? props.externalData! : internal.data,
    loading: !isExt && internal.loading,
    error: props.externalError ?? (isExt ? null : internal.error),
    reload: props.externalReload ?? internal.reload,
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
          <h2 className="font-semibold text-slate-900">Live Aktivität</h2>
        </div>
        <select value={modul} onChange={e => setModul(e.target.value)}
                className="text-xs border border-slate-200 rounded px-2 py-1">
          <option value="">Alle Module</option>
          <option value="drueckteile">Drückteile</option>
          <option value="laufraeder">Laufräder</option>
          <option value="baugruppen">Baugruppen</option>
          <option value="montage">Montage</option>
        </select>
      </div>
      <div className="max-h-[360px] overflow-y-auto">
        {feed.loading && <div className="p-5 text-slate-500 text-sm">Lädt …</div>}
        {feed.error && <ErrorBanner error={feed.error} onRetry={() => feed.reload()} compact />}
        {feed.data && feed.data.data.length === 0 && (
          <div className="p-5 text-slate-500 text-sm text-center">Noch keine Aktivität heute</div>
        )}
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            {feed.data?.data.map((e: FeedEvent, i: number) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-2 w-20 text-xs text-slate-500 whitespace-nowrap">
                  {formatTime(e.at)}
                </td>
                <td className="px-4 py-2 w-24">
                  {e.event === 'session_start' && <span className="text-sky-700 font-bold">▶ Start</span>}
                  {e.event === 'session_end'   && <span className="text-amber-600 font-bold">⏹ Stop</span>}
                  {e.event === 'fertig'        && <span className="text-emerald-700 font-bold">✓ Fertig</span>}
                  {e.event === 'stand_change'  && <span className="text-purple-700 font-bold">→ Stand</span>}
                </td>
                <td className="px-4 py-2 font-medium">
                  {(e.user_name ?? '—').replace(/^\[WS\]\s*/, '')}
                </td>
                <td className="px-4 py-2 text-xs">
                  {e.event === 'stand_change' && e.new_stand_wert ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border"
                          style={{
                            background: `${e.new_stand_farbe ?? '#64748B'}30`,
                            borderColor: `${e.new_stand_farbe ?? '#64748B'}80`,
                            color: darken(e.new_stand_farbe),
                          }}>
                      → {e.new_stand_wert}
                    </span>
                  ) : e.abteilung_name && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
                          style={{ background: `${e.abteilung_farbe}30`, color: darken(e.abteilung_farbe) }}>
                      {e.abteilung_name}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-600">
                  <Link to={`/${e.modul}/${e.record_id}`} className="hover:text-brand hover:underline">
                    {e.bestellnr
                      ? <><span className="font-medium">{e.kunde_name ?? MODULE_LABELS[e.modul]}</span> · {e.bestellnr}</>
                      : <>{MODULE_LABELS[e.modul]} · #{e.record_id}</>}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AktiveArbeiterWidget(props: ExternalProps<{ data: ArbeitSession[] }> = {}) {
  const navigate = useNavigate();
  const isExt = props.externalData !== undefined;
  const internal = useApi(() => api.sessions.active(), [], !isExt);
  useAutoRefresh(() => internal.reload(), 5_000, !isExt);
  const active = {
    data: isExt ? props.externalData! : internal.data,
    loading: !isExt && internal.loading,
    error: props.externalError ?? (isExt ? null : internal.error),
    reload: props.externalReload ?? internal.reload,
  };
  const items = active.data?.data ?? [];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Aktive Arbeiter</h2>
        <span className="text-xs text-slate-500">{items.length} aktiv</span>
      </div>
      <div className="max-h-[360px] overflow-y-auto">
        {active.loading && <div className="p-5 text-slate-500 text-sm">Lädt …</div>}
        {active.error && <ErrorBanner error={active.error} onRetry={() => active.reload()} compact />}
        {!active.error && items.length === 0 && (
          <div className="p-6 text-center text-slate-500 text-sm">Niemand arbeitet gerade aktiv</div>
        )}
        <div className="divide-y divide-slate-100">
          {items.map((s: ArbeitSession) => {
            const abteilungDisplay = (s.user_name ?? '').replace(/^\[WS\]\s*/, '');
            return (
            <div key={s.id}
                 onClick={() => navigate(`/${s.module_table}/${s.record_id}`)}
                 className="px-5 py-3 hover:bg-slate-50 cursor-pointer transition">
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-2 h-2 rounded-full animate-pulse flex-shrink-0"
                        style={{ background: s.abteilung_farbe ?? '#10B981' }}></span>
                  {/* Abteilung adı — tıklayınca abteilung detaya */}
                  {s.abteilung_id ? (
                    <button onClick={e => { e.stopPropagation(); navigate(`/abteilung/${s.abteilung_id}`); }}
                            className="font-semibold hover:underline"
                            style={{ color: darken(s.abteilung_farbe) }}
                            title="Abteilung öffnen">
                      {abteilungDisplay}
                    </button>
                  ) : (
                    <span className="font-semibold" style={{ color: darken(s.abteilung_farbe) }}>
                      {abteilungDisplay}
                    </span>
                  )}
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700 tracking-wide">
                    {MODULE_LABELS[s.module_table] ?? s.module_table}
                  </span>
                </div>
                <span className="text-xs text-slate-500 font-mono whitespace-nowrap">
                  {formatDuration(s.running_minutes ?? 0)}
                </span>
              </div>
              <div className="text-sm text-slate-700 ml-4">
                {s.auftrag ? (
                  <>
                    <span className="font-medium">{s.auftrag.kunde_name}</span> · {s.auftrag.bestellnr}
                    <span className="text-slate-500"> — {s.auftrag.bezeichnung ?? '—'}</span>
                  </>
                ) : (
                  <span className="text-slate-400">Kayıt bulunamadı</span>
                )}
              </div>
            </div>
          );})}
        </div>
      </div>
    </div>
  );
}

export function AbteilungHeatmapWidget(props: ExternalProps<{ data: HeatmapEntry[] }> = {}) {
  const isExt = props.externalData !== undefined;
  const internal = useApi(() => api.sessions.heatmap(), [], !isExt);
  useAutoRefresh(() => internal.reload(), 5_000, !isExt);
  const heatmap = {
    data: isExt ? props.externalData! : internal.data,
    loading: !isExt && internal.loading,
    error: props.externalError ?? (isExt ? null : internal.error),
    reload: props.externalReload ?? internal.reload,
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200">
        <h2 className="font-semibold text-slate-900">Abteilungen — Offene Aufträge</h2>
      </div>
      {heatmap.error && <ErrorBanner error={heatmap.error} onRetry={() => heatmap.reload()} compact />}
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {heatmap.data?.data.map((h: HeatmapEntry) => (
          <Link key={h.id} to={`/abteilung/${h.id}`}
               className="block rounded-lg p-3 border transition hover:shadow-md hover:scale-[1.02] cursor-pointer"
               style={{ background: `${h.farbe}12`, borderColor: `${h.farbe}55` }}>
            <div className="text-[11px] uppercase font-semibold tracking-wide truncate" style={{ color: darken(h.farbe) }}>
              {h.name}
            </div>
            <div className="text-4xl font-bold mt-1 tabular-nums" style={{ color: darken(h.farbe) }}>
              {h.offene_auftraege}
            </div>
            <div className="text-[10px] text-slate-600 uppercase mb-2">
              {h.main_module ? 'Offene Aufträge' : 'Aktiv (Sessions)'}
            </div>
            <div className="flex items-center justify-between text-xs border-t pt-2 gap-2" style={{ borderColor: `${h.farbe}40` }}>
              <span title="Aktive Sessions">
                {h.aktive_sessions > 0 ? (
                  <><span className="font-bold" style={{ color: darken(h.farbe) }}>▶ {h.aktive_sessions}</span>
                    <span className="text-slate-500"> aktiv</span></>
                ) : <span className="text-slate-400">— aktiv</span>}
              </span>
              <span title="Heute fertig">
                {h.heute_fertig_sessions > 0 ? (
                  <span className="text-emerald-700 font-semibold">✓ {h.heute_fertig_sessions}</span>
                ) : <span className="text-slate-400">—</span>}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function QSWidget(props: ExternalProps<{ count: ProblemCount; list: ProblemMeldung[] }> = {}) {
  const isExt = props.externalData !== undefined;
  const countInternal = useApi(() => api.problems.count(), [], !isExt);
  const listInternal  = useApi(() => api.problems.list({ status: 'offen', limit: 5 }), [], !isExt);
  useAutoRefresh(() => { countInternal.reload(); listInternal.reload(); }, 5_000, !isExt);

  const count = isExt
    ? { data: { data: props.externalData!.count }, loading: false, error: props.externalError, reload: props.externalReload ?? (() => {}) }
    : countInternal;
  const list = isExt
    ? { data: { data: props.externalData!.list }, loading: false, error: props.externalError, reload: props.externalReload ?? (() => {}) }
    : listInternal;

  const c = count.data?.data;
  const offen = c?.offen ?? 0;
  const kritisch = c?.kritisch ?? 0;

  return (
    <div className={`bg-white rounded-xl border overflow-hidden ${kritisch > 0 ? 'border-red-300 ring-2 ring-red-200' : 'border-slate-200'}`}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${offen > 0 ? 'bg-red-500 animate-pulse' : 'bg-slate-300'}`} />
          <h2 className="font-semibold text-slate-900">QS-Meldungen</h2>
        </div>
        <Link to="/qs" className="text-xs text-brand hover:underline">Alle →</Link>
      </div>
      {(count.error || list.error) ? (
        <ErrorBanner error={count.error ?? list.error ?? null} onRetry={() => { count.reload(); list.reload(); }} compact />
      ) : null}
      <div className="p-4">
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="bg-red-50 border border-red-200 rounded p-2">
            <div className="text-[10px] uppercase text-red-800 font-semibold">Offen</div>
            <div className="text-2xl font-bold text-red-700 tabular-nums">{offen}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-2">
            <div className="text-[10px] uppercase text-amber-800 font-semibold">In Bearb.</div>
            <div className="text-2xl font-bold text-amber-700 tabular-nums">{c?.in_bearbeitung ?? 0}</div>
          </div>
          <div className="bg-sky-50 border border-sky-200 rounded p-2">
            <div className="text-[10px] uppercase text-sky-800 font-semibold">Heute neu</div>
            <div className="text-2xl font-bold text-sky-700 tabular-nums">{c?.heute_neu ?? 0}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
            <div className="text-[10px] uppercase text-emerald-800 font-semibold">Heute gelöst</div>
            <div className="text-2xl font-bold text-emerald-700 tabular-nums">{c?.heute_geloest ?? 0}</div>
          </div>
        </div>
        {list.data && list.data.data.length > 0 && (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {list.data.data.map(p => (
              <Link key={p.id} to={`/qs?id=${p.id}`}
                    className={`block p-2 rounded text-sm hover:bg-slate-100 border border-transparent hover:border-slate-300 cursor-pointer ${p.priority === 'kritisch' ? 'border-l-4 border-l-red-600 bg-red-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">{p.kategorie}: {p.auftrag?.kunde_name ?? ''} · {p.auftrag?.bestellnr}</span>
                  {p.priority !== 'normal' && (
                    <span className={`text-[10px] uppercase font-bold px-1.5 rounded ${
                      p.priority === 'kritisch' ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'
                    }`}>{p.priority}</span>
                  )}
                </div>
                {p.beschreibung && <div className="text-xs text-slate-500 truncate mt-0.5">{p.beschreibung}</div>}
              </Link>
            ))}
          </div>
        )}
        {list.data && list.data.data.length === 0 && (
          <div className="text-center py-4 text-slate-400 text-sm">Keine offenen Meldungen — alles gut 👍</div>
        )}
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.floor(minutes)}min`;
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return `${h}h ${m}min`;
}

function darken(hex: string | null | undefined): string {
  if (!hex || !hex.startsWith('#')) return '#1E293B';
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xFF) * 0.55));
  const g = Math.max(0, Math.round(((n >> 8)  & 0xFF) * 0.55));
  const b = Math.max(0, Math.round((n & 0xFF) * 0.55));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
